import PropTypes from 'prop-types';
import styled from 'styled-components';

import { CircularProgress, MenuItem, TextField } from '@mui/material';

import { CircularProgressWrapper } from 'globalStyles';
import { fullOfficialName } from 'utils/helpers';

const StyledTextField = styled(TextField)`
  width: 100%;
  padding-bottom: 15px;
`;

/**
 * Cascading selects for picking a government official: category
 * (Federal/State), then position or state, then the specific official's term,
 * with an optional relationship description. Use in target selection,
 * contact-official relationships, and filtered-list filter dialogs; all state
 * and option rendering is supplied by the parent.
 */
const OfficialsDetails = ({
  administrativeLevels,
  category,
  description,
  handleCategoryChange,
  handleNonFederalPositionChange,
  handlePositionStateChange,
  isFilteredListAdding,
  isTermsFetching,
  onlyPositions,
  position,
  positionTerms,
  renderPositions,
  renderStates,
  setDescription,
  setFilterValue,
  setPosition,
  setState,
  setTerm,
  state,
  term,
}) => (
  <>
    <StyledTextField
      label="Category"
      onChange={event => {
        setState('');
        setPosition('');
        setTerm({});
        setFilterValue('');
        handleCategoryChange(event);
      }}
      select
      value={category}
      variant="standard"
    >
      {administrativeLevels.map(element => (
        <MenuItem key={element.key} value={element.value}>
          {element.value}
        </MenuItem>
      ))}
    </StyledTextField>
    {category && (
      <StyledTextField
        select
        label={category === 'Federal' ? 'Position' : 'State'}
        value={category === 'Federal' ? position : state}
        onChange={event => {
          setPosition('');
          setTerm({});
          setFilterValue('');
          handlePositionStateChange(event);
        }}
        variant="standard"
      >
        {category === 'Federal' ? renderPositions() : renderStates()}
      </StyledTextField>
    )}
    {state && category !== 'Federal' && (
      <StyledTextField
        disabled={!renderPositions().length}
        label="Position"
        onChange={event => handleNonFederalPositionChange(event)}
        select
        value={position}
      >
        {renderPositions()}
      </StyledTextField>
    )}
    {position && !onlyPositions && (
      <StyledTextField
        label="Term"
        onChange={event => {
          setTerm(event.target.value);
          setFilterValue(event.target.value.uuid);
        }}
        select
        value={Object.keys(term).length ? term : ''}
        slotProps={{
          select: {
            renderValue: option => option.name,
          },
        }}
        variant="standard"
      >
        {isTermsFetching ? (
          <CircularProgressWrapper>
            <CircularProgress />
          </CircularProgressWrapper>
        ) : (
          positionTerms.map(({ uuid, office, official }) => {
            const name = fullOfficialName(official, office?.title);

            return (
              <MenuItem
                key={uuid}
                value={{
                  name,
                  uuid: official.uuid,
                }}
              >
                {name}
              </MenuItem>
            );
          })
        )}
      </StyledTextField>
    )}
    {!isFilteredListAdding && term?.uuid && !onlyPositions && (
      <StyledTextField
        defaultValue={description}
        label="Description"
        multiline
        onChange={event => setDescription(event.target.value)}
        placeholder="Tell us how they know each other"
        variant="standard"
      />
    )}
  </>
);

OfficialsDetails.defaultProps = {
  administrativeLevels: [],
  category: '',
  description: '',
  handleCategoryChange: () => {},
  handleNonFederalPositionChange: () => {},
  handlePositionStateChange: () => {},
  isFilteredListAdding: false,
  isTermsFetching: false,
  onlyPositions: false,
  position: '',
  positionTerms: [],
  renderPositions: () => {},
  renderStates: () => {},
  setDescription: () => {},
  setFilterValue: () => {},
  setPosition: () => {},
  setState: () => {},
  setTerm: () => {},
  state: '',
  term: {},
};

OfficialsDetails.propTypes = {
  /** Options for the Category select ({ key, value } pairs, e.g. Federal, State). */
  administrativeLevels: PropTypes.arrayOf(PropTypes.shape({})),
  category: PropTypes.string,
  /** Free-text note on how the contact knows the official. */
  description: PropTypes.string,
  handleCategoryChange: PropTypes.func,
  handleNonFederalPositionChange: PropTypes.func,
  handlePositionStateChange: PropTypes.func,
  /** Hide the Description field, as when adding a filtered-list filter. */
  isFilteredListAdding: PropTypes.bool,
  isTermsFetching: PropTypes.bool,
  /** Stop the cascade at position: hide the Term and Description fields. */
  onlyPositions: PropTypes.bool,
  position: PropTypes.string,
  /** Term options; each { uuid, office, official } renders as the official's full name. */
  positionTerms: PropTypes.arrayOf(PropTypes.shape({})),
  /** Returns the `MenuItem` list for the Position select. */
  renderPositions: PropTypes.func,
  /** Returns the `MenuItem` list for the State select. */
  renderStates: PropTypes.func,
  setDescription: PropTypes.func,
  /** Called with the selected official's uuid (or '' when the cascade resets). */
  setFilterValue: PropTypes.func,
  setPosition: PropTypes.func,
  setState: PropTypes.func,
  setTerm: PropTypes.func,
  state: PropTypes.string,
  term: PropTypes.shape({
    name: PropTypes.string,
    uuid: PropTypes.string,
  }),
};

export default OfficialsDetails;
